import { useEffect, useRef, useState, useCallback } from 'react';

// Hook to detect when element enters viewport
export const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { threshold = 0.1, rootMargin = '0px 0px -50px 0px' } = options;

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(currentRef);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(currentRef);

    return () => {
      observer.unobserve(currentRef);
    };
  }, [threshold, rootMargin]);

  return [ref, isVisible];
};

// Hook for parallax effect on scroll - disabled on mobile for performance
export const useParallax = (speed = 0.5) => {
  const ref = useRef(null);

  useEffect(() => {
    const currentRef = ref.current;
    
    // Disable parallax on mobile/touch devices for better performance
    const isMobile = window.matchMedia('(max-width: 768px)').matches || 
                     'ontouchstart' in window ||
                     navigator.maxTouchPoints > 0;
    
    if (isMobile) {
      if (currentRef) {
        currentRef.style.setProperty('--parallax-offset', '0px');
      }
      return;
    }

    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (currentRef) {
            const scrollY = window.scrollY;
            const offset = scrollY * speed;
            currentRef.style.setProperty('--parallax-offset', `${offset}px`);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return ref;
};

// Hook for mouse follow effect
export const useMouseFollow = () => {
  const ref = useRef(null);

  const handleMouseMove = useCallback((e) => {
    const element = ref.current;
    if (!element) return;
    
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    element.style.setProperty('--mouse-x', `${x * 0.1}px`);
    element.style.setProperty('--mouse-y', `${y * 0.1}px`);
  }, []);

  const handleMouseLeave = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    
    element.style.setProperty('--mouse-x', '0px');
    element.style.setProperty('--mouse-y', '0px');
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseMove, handleMouseLeave]);

  return ref;
};

export default useScrollReveal;
